import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    FormControlLabel,
    IconButton,
    InputAdornment,
    makeStyles,
    Switch,
    TextField,
    Theme,
    Typography,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import SearchIcon from '@material-ui/icons/Search'
import ScheduleIcon from '@material-ui/icons/Schedule'
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { axiosInstance } from '../../helpers'
import { User } from '../../models'
import { useContractAlertEmailSettings } from '../../hooks/useContractAlertEmailSettings'

const LEAD_PRESETS = [7, 14, 30, 60, 90, 120]

const normalizeDaysBefore = (value?: number[] | number | null) => {
    const raw = Array.isArray(value) ? value : value != null ? [value] : []
    const unique = Array.from(
        new Set(
            raw
                .map((item) => Number(item))
                .filter(
                    (item) =>
                        Number.isInteger(item) && item >= 1 && item <= 365,
                ),
        ),
    ).sort((a, b) => b - a)
    return unique.length > 0 ? unique : [30]
}

const leadShort = (days: number) => {
    if (days === 1) return '1 day'
    if (days === 7) return '1 week'
    if (days === 14) return '2 weeks'
    return `${days} days`
}

const leadLabel = (daysList: number[]) => {
    const parts = normalizeDaysBefore(daysList).map(leadShort)
    if (parts.length === 1) return `${parts[0]} before expiration`
    if (parts.length === 2)
        return `${parts[0]} and ${parts[1]} before expiration`
    return `${parts.slice(0, -1).join(', ')}, and ${
        parts[parts.length - 1]
    } before expiration`
}

const presetLabel = (days: number) => {
    if (days === 7) return '1 week'
    if (days === 14) return '2 weeks'
    return `${days} days`
}

const initialsFor = (name: string) =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('') || '?'

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        borderRadius: theme.spacing(1.5),
        overflow: 'hidden',
        width: '100%',
        maxWidth: 760,
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        padding: theme.spacing(2.5, 3, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    titleRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        minWidth: 0,
    },
    icon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: '#eff6ff',
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    title: {
        fontSize: 20,
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.25,
    },
    subtitle: {
        marginTop: 4,
        color: theme.palette.text.secondary,
    },
    closeButton: {
        backgroundColor: '#f3f4f6',
        marginTop: 2,
    },
    content: {
        padding: theme.spacing(2.5, 3, 1),
        minHeight: 560,
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        minHeight: 520,
    },
    bannerSlot: {
        minHeight: 56,
    },
    banner: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.25, 1.5),
        borderRadius: theme.spacing(1.25),
        border: '1px solid #bfdbfe',
        backgroundColor: '#eff6ff',
    },
    bannerOverride: {
        border: '1px solid #fde68a',
        backgroundColor: '#fffbeb',
    },
    bannerText: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    enableCard: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        padding: theme.spacing(1.5, 2),
        borderRadius: theme.spacing(1.25),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
    },
    enableCardOn: {
        borderColor: '#86efac',
        backgroundColor: '#f0fdf4',
    },
    section: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.25),
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1.25, 1.75),
        backgroundColor: theme.palette.grey[50],
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    sectionIcon: {
        color: theme.palette.text.secondary,
        fontSize: 18,
    },
    sectionTitle: {
        fontWeight: 600,
        fontSize: 13,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        flex: 1,
    },
    sectionBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.75),
    },
    row: {
        display: 'flex',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
    },
    field: {
        minWidth: 140,
        flex: 1,
    },
    peopleSearch: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.paper,
        },
    },
    peopleList: {
        height: 220,
        overflowY: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
    },
    personRow: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        margin: 0,
        padding: theme.spacing(0.75, 1.25, 0.75, 0.75),
        '&:not(:last-child)': {
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    avatar: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: '#dbeafe',
        color: '#1d4ed8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 700,
        flexShrink: 0,
        marginRight: theme.spacing(1.25),
    },
    personCopy: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        marginLeft: theme.spacing(0.5),
    },
    personName: {
        fontWeight: 600,
        fontSize: 14,
        lineHeight: 1.25,
    },
    personEmail: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
    },
    presetChip: {
        height: 28,
        fontWeight: 600,
    },
    presetChipActive: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:focus, &:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    lastSent: {
        color: theme.palette.text.secondary,
        padding: theme.spacing(0, 0.5),
        minHeight: 20,
    },
    actions: {
        padding: theme.spacing(1.5, 3, 2),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}))

interface ContractAlertEmailSettingsDialogProps {
    open: boolean
    onClose: () => void
    organization: number | null
    apartment?: number | null
    users?: User[]
    scopeLabel?: string
}

export const ContractAlertEmailSettingsDialog: React.FC<ContractAlertEmailSettingsDialogProps> = ({
    open,
    onClose,
    organization,
    apartment = null,
    users: usersProp,
    scopeLabel,
}) => {
    const classes = useStyles()
    const {
        settings,
        isLoading,
        updateSettings,
        sendNow,
        resetToOrganization,
    } = useContractAlertEmailSettings({
        organization,
        apartment,
        enabled: open && organization != null,
    })
    const [enabled, setEnabled] = useState(false)
    const [daysBefore, setDaysBefore] = useState<number[]>([30])
    const [customDays, setCustomDays] = useState('')
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
    const [peopleSearch, setPeopleSearch] = useState('')
    const [loadedUsers, setLoadedUsers] = useState<User[]>([])

    useEffect(() => {
        if (!open || organization == null || usersProp) return
        let cancelled = false
        axiosInstance
            .get<User[]>('user/', { params: { organization } })
            .then((res) => {
                if (!cancelled) setLoadedUsers(res.data || [])
            })
            .catch(() => {
                if (!cancelled) setLoadedUsers([])
            })
        return () => {
            cancelled = true
        }
    }, [open, organization, usersProp])

    useEffect(() => {
        if (!open) {
            setPeopleSearch('')
            setCustomDays('')
        }
    }, [open])

    const users = usersProp ?? loadedUsers

    useEffect(() => {
        if (!settings) return
        setEnabled(settings.enabled)
        setDaysBefore(normalizeDaysBefore(settings.days_before))
        setSelectedUserIds(settings.recipient_users.map((user) => user.id))
    }, [settings])

    const visibleUsers = useMemo(() => {
        const search = peopleSearch.trim().toLowerCase()
        if (!search) return users
        return users.filter(
            (user) =>
                user.name.toLowerCase().includes(search) ||
                (user.email || '').toLowerCase().includes(search),
        )
    }, [users, peopleSearch])

    const addCustomDays = () => {
        const next = Number(customDays)
        if (!customDays.trim() || !Number.isInteger(next)) return
        const clamped = Math.min(365, Math.max(1, next))
        setDaysBefore((current) => normalizeDaysBefore([...current, clamped]))
        setCustomDays('')
    }

    const handleSave = () => {
        updateSettings.mutate(
            {
                enabled,
                days_before: normalizeDaysBefore(daysBefore),
                include_property_manager: false,
                recipient_user_ids: selectedUserIds,
                extra_emails: [],
            },
            { onSuccess: onClose },
        )
    }

    const recipientCount = selectedUserIds.length
    const isPropertyOverride =
        apartment != null &&
        settings != null &&
        !settings.inherited &&
        settings.apartment != null
    const scheduleSummary = leadLabel(daysBefore)
    const lastSent = settings?.last_sent_on
        ? new Date(`${settings.last_sent_on}T00:00:00`).toLocaleDateString(
              undefined,
              {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
              },
          )
        : null

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            classes={{ paper: classes.paper }}
        >
            <Box className={classes.header}>
                <Box className={classes.titleRow}>
                    <Box className={classes.icon}>
                        <MailOutlineIcon />
                    </Box>
                    <Box minWidth={0}>
                        <Typography className={classes.title}>
                            Alert email settings
                        </Typography>
                        {scopeLabel ? (
                            <Typography
                                variant="body2"
                                className={classes.subtitle}
                            >
                                {scopeLabel}
                            </Typography>
                        ) : null}
                    </Box>
                </Box>
                <IconButton
                    size="small"
                    className={classes.closeButton}
                    onClick={onClose}
                    aria-label="Close email settings"
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <DialogContent className={classes.content}>
                <Box className={classes.form}>
                    {apartment != null && (
                        <Box className={classes.bannerSlot}>
                            {settings?.inherited && (
                                <Box className={classes.banner}>
                                    <Box className={classes.bannerText}>
                                        <InfoOutlinedIcon fontSize="small" />
                                        <Typography variant="body2">
                                            This property is using the
                                            organization default. Save to
                                            customize who gets emailed here.
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            {isPropertyOverride && (
                                <Box
                                    className={`${classes.banner} ${classes.bannerOverride}`}
                                >
                                    <Box className={classes.bannerText}>
                                        <InfoOutlinedIcon fontSize="small" />
                                        <Typography variant="body2">
                                            This property has its own email
                                            settings and will not use the
                                            organization default.
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        color="primary"
                                        onClick={() =>
                                            resetToOrganization.mutate()
                                        }
                                        disabled={resetToOrganization.isLoading}
                                    >
                                        Use org default
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}

                    <Box
                        className={`${classes.enableCard} ${
                            enabled ? classes.enableCardOn : ''
                        }`}
                    >
                        <Box>
                            <Typography style={{ fontWeight: 600 }}>
                                Scheduled emails
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {enabled
                                    ? scheduleSummary
                                    : 'Off until you turn this on'}
                            </Typography>
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={enabled}
                                    onChange={(e) =>
                                        setEnabled(e.target.checked)
                                    }
                                    color="primary"
                                />
                            }
                            label={enabled ? 'On' : 'Off'}
                        />
                    </Box>

                    <Box className={classes.section}>
                        <Box className={classes.sectionHeader}>
                            <ScheduleIcon className={classes.sectionIcon} />
                            <Typography className={classes.sectionTitle}>
                                When to send
                            </Typography>
                        </Box>
                        <Box className={classes.sectionBody}>
                            <Typography variant="body2" color="textSecondary">
                                Choose one or more reminders. A contract is
                                emailed on each selected day. Auto-renewals stay
                                on the list through their notice window, and
                                expired contracts are included.
                            </Typography>
                            <Box className={classes.chips}>
                                {LEAD_PRESETS.map((days) => {
                                    const selected = daysBefore.includes(days)
                                    return (
                                        <Chip
                                            key={days}
                                            size="small"
                                            label={presetLabel(days)}
                                            clickable
                                            onClick={() =>
                                                setDaysBefore((current) => {
                                                    if (selected) {
                                                        const next = current.filter(
                                                            (item) =>
                                                                item !== days,
                                                        )
                                                        return next.length
                                                            ? next
                                                            : current
                                                    }
                                                    return normalizeDaysBefore([
                                                        ...current,
                                                        days,
                                                    ])
                                                })
                                            }
                                            className={`${classes.presetChip} ${
                                                selected
                                                    ? classes.presetChipActive
                                                    : ''
                                            }`}
                                        />
                                    )
                                })}
                            </Box>
                            {daysBefore.some(
                                (days) => !LEAD_PRESETS.includes(days),
                            ) && (
                                <Box className={classes.chips}>
                                    {daysBefore
                                        .filter(
                                            (days) =>
                                                !LEAD_PRESETS.includes(days),
                                        )
                                        .map((days) => (
                                            <Chip
                                                key={days}
                                                size="small"
                                                label={presetLabel(days)}
                                                onDelete={() =>
                                                    setDaysBefore((current) => {
                                                        const next = current.filter(
                                                            (item) =>
                                                                item !== days,
                                                        )
                                                        return next.length
                                                            ? next
                                                            : current
                                                    })
                                                }
                                                className={`${classes.presetChip} ${classes.presetChipActive}`}
                                            />
                                        ))}
                                </Box>
                            )}
                            <Box className={classes.row}>
                                <TextField
                                    className={classes.field}
                                    label="Add custom days"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={customDays}
                                    onChange={(e) =>
                                        setCustomDays(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key !== 'Enter') return
                                        e.preventDefault()
                                        addCustomDays()
                                    }}
                                    inputProps={{ min: 1, max: 365 }}
                                    helperText="1 to 365 days."
                                />
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={addCustomDays}
                                    disabled={!customDays.trim()}
                                    style={{ height: 40, marginTop: 0 }}
                                >
                                    Add
                                </Button>
                            </Box>
                        </Box>
                    </Box>

                    <Box className={classes.section}>
                        <Box className={classes.sectionHeader}>
                            <PeopleOutlineIcon
                                className={classes.sectionIcon}
                            />
                            <Typography className={classes.sectionTitle}>
                                Who gets emailed
                            </Typography>
                            <Chip
                                size="small"
                                label={`${recipientCount} recipient${
                                    recipientCount === 1 ? '' : 's'
                                }`}
                            />
                        </Box>
                        <Box className={classes.sectionBody}>
                            <Typography variant="body2" color="textSecondary">
                                Each person is only emailed for properties they
                                are attached to.
                            </Typography>
                            <TextField
                                className={classes.peopleSearch}
                                placeholder="Search people..."
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={peopleSearch}
                                onChange={(e) =>
                                    setPeopleSearch(e.target.value)
                                }
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Box className={classes.peopleList}>
                                {visibleUsers.length === 0 ? (
                                    <Box padding={2}>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            No people found.
                                        </Typography>
                                    </Box>
                                ) : (
                                    visibleUsers.map((user) => (
                                        <FormControlLabel
                                            key={user.id}
                                            className={classes.personRow}
                                            control={
                                                <Checkbox
                                                    checked={selectedUserIds.includes(
                                                        user.id,
                                                    )}
                                                    onChange={(e) =>
                                                        setSelectedUserIds(
                                                            (current) =>
                                                                e.target.checked
                                                                    ? [
                                                                          ...current,
                                                                          user.id,
                                                                      ]
                                                                    : current.filter(
                                                                          (
                                                                              id,
                                                                          ) =>
                                                                              id !==
                                                                              user.id,
                                                                      ),
                                                        )
                                                    }
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    minWidth={0}
                                                >
                                                    <Box
                                                        className={
                                                            classes.avatar
                                                        }
                                                    >
                                                        {initialsFor(user.name)}
                                                    </Box>
                                                    <Box
                                                        className={
                                                            classes.personCopy
                                                        }
                                                    >
                                                        <Typography
                                                            className={
                                                                classes.personName
                                                            }
                                                        >
                                                            {user.name}
                                                        </Typography>
                                                        {user.email ? (
                                                            <Typography
                                                                className={
                                                                    classes.personEmail
                                                                }
                                                            >
                                                                {user.email}
                                                            </Typography>
                                                        ) : null}
                                                    </Box>
                                                </Box>
                                            }
                                        />
                                    ))
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Typography variant="caption" className={classes.lastSent}>
                        {lastSent ? `Last sent ${lastSent}` : '\u00a0'}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button
                    variant="outlined"
                    onClick={() => sendNow.mutate()}
                    disabled={
                        sendNow.isLoading ||
                        organization == null ||
                        (isLoading && !settings)
                    }
                >
                    {sendNow.isLoading ? 'Sending…' : 'Send now'}
                </Button>
                <Box flex={1} />
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleSave}
                    disabled={
                        updateSettings.isLoading ||
                        organization == null ||
                        (isLoading && !settings)
                    }
                >
                    {updateSettings.isLoading ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
